import { useEffect, useRef, useState } from 'react';

// bycursor-start
/**
 * @description CapiIcon 动画控制 Hook 的选项
 * @author bycursor
 */
interface UseCapiIconAnimationOptions {
  /** 气泡文本，用于判断是否启动气泡动画 */
  bubbleText?: string;
  /** 拖拽状态，用于监听暂停/恢复动画 */
  moving: boolean;
  /** 是否自动启动（默认 true） */
  autoStart?: boolean;
}

/**
 * @description CapiIcon 动画控制 Hook 的返回值
 * @author bycursor
 */
interface UseCapiIconAnimationReturn {
  /** 气泡显示状态 */
  bubbleVisible: boolean;
  /** 图标半隐藏状态 */
  iconHalfHidden: boolean;
  /** 重置所有动画 */
  resetAllAnimations: () => void;
  /** 启动气泡动画 */
  startBubbleAnimation: () => void;
  /** 启动图标动画 */
  startIconAnimation: () => void;
}

/**
 * @description CapiIcon 动画控制 Hook
 * @author bycursor
 * @param options 配置选项
 * @returns 动画状态和控制函数
 */
function useCapiIconAnimation(options: UseCapiIconAnimationOptions): UseCapiIconAnimationReturn {
  const { bubbleText, moving, autoStart = true } = options;

  const [bubbleVisible, setBubbleVisible] = useState(false);
  const [iconHalfHidden, setIconHalfHidden] = useState(false);
  const bubbleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const iconTimerRef = useRef<NodeJS.Timeout | null>(null);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * @description 重置所有动画状态和定时器
   * @author bycursor
   */
  const resetAllAnimations = () => {
    // 清除所有定时器
    if (bubbleTimerRef.current) {
      clearTimeout(bubbleTimerRef.current);
      bubbleTimerRef.current = null;
    }
    if (iconTimerRef.current) {
      clearTimeout(iconTimerRef.current);
      iconTimerRef.current = null;
    }
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
    // 重置状态
    setBubbleVisible(false);
    setIconHalfHidden(false);
  };

  /**
   * @description 启动气泡动画：3s后淡入，再过10s后淡出
   * @author bycursor
   */
  const startBubbleAnimation = () => {
    if (!bubbleText) return;
    // 清除气泡定时器
    if (bubbleTimerRef.current) {
      clearTimeout(bubbleTimerRef.current);
      bubbleTimerRef.current = null;
    }
    // 重置气泡状态
    setBubbleVisible(false);
    // 3s后淡入
    bubbleTimerRef.current = setTimeout(() => {
      setBubbleVisible(true);
      // 再过10s后淡出
      bubbleTimerRef.current = setTimeout(() => {
        setBubbleVisible(false);
      }, 10000);
    }, 3000);
  };

  /**
   * @description 启动图标半隐藏动画：静置16s后，0.5s动画半隐藏到左/右边界
   * @author bycursor
   */
  const startIconAnimation = () => {
    // 清除图标定时器
    if (iconTimerRef.current) {
      clearTimeout(iconTimerRef.current);
      iconTimerRef.current = null;
    }
    // 重置图标状态
    setIconHalfHidden(false);
    // 16s后触发半隐藏
    iconTimerRef.current = setTimeout(() => {
      setIconHalfHidden(true);
    }, 16000);
  };

  // 组件挂载时自动启动动画
  useEffect(() => {
    if (autoStart) {
      startBubbleAnimation();
      startIconAnimation();
    }
    // 组件卸载时清理定时器
    return () => {
      resetAllAnimations();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  // 监听 moving 状态：拖拽时暂停动画，结束时恢复
  useEffect(() => {
    if (moving) {
      // 拖拽时清除所有定时器（暂停动画）
      resetAllAnimations();
    } else {
      // 拖拽结束后，延迟300ms后重新启动动画
      resetTimerRef.current = setTimeout(() => {
        startBubbleAnimation();
        startIconAnimation();
      }, 300);
    }
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moving]);

  return {
    bubbleVisible,
    iconHalfHidden,
    resetAllAnimations,
    startBubbleAnimation,
    startIconAnimation,
  };
}
// bycursor-end

export default useCapiIconAnimation;
// Contains cursor-generated edits.
